import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ClientStatus, MonthlyReport, SessionDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { dayLabel, elapsed, euro, fmtClock, fmtLongDate, fmtTime, hm, localDay, monthLabel, thisMonth, todayKey, typeLabel } from '../format';
import { Calendar, ClockCard, Empty, ErrorBanner, NoteRow, Panel, Section, Seg, SessionsList, Stat, useNow, useSections } from '../ui';

type Filter = 'live' | 'month' | 'day';

/** Dashboard del cliente: quando il consulente è online, le note in tempo reale e lo storico. */
export function ClientView({ ctx }: { ctx: Ctx }) {
  const { api, me, event, eventSeq } = ctx;
  const [status, setStatus] = useState<ClientStatus | null>(null);
  const [month, setMonth] = useState(thisMonth());
  const [report, setReport] = useState<MonthlyReport | null>(null);
  const [filter, setFilter] = useState<Filter>('live');
  const [day, setDay] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sections = useSections({ notes: true, today: true });
  const now = useNow(1000);
  const today = todayKey();

  const loadStatus = useCallback(
    () =>
      api
        .get<ClientStatus>('/api/client/status')
        .then((s) => {
          setStatus(s);
          setError(null);
        })
        .catch((e) => setError((e as Error).message)),
    [api],
  );

  const loadReport = useCallback(
    () =>
      api
        .get<MonthlyReport>(`/api/client/sessions?month=${month}`)
        .then(setReport)
        .catch((e) => setError((e as Error).message)),
    [api, month],
  );

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);
  useEffect(() => {
    loadReport();
  }, [loadReport]);

  useEffect(() => {
    if (!event) return;
    if (event.type === 'session.started') loadStatus();
    else if (event.type === 'session.stopped') {
      loadStatus();
      loadReport();
    } else if (event.type === 'note.added') {
      const added = event.note;
      setStatus((s) =>
        s?.session && s.session.id === added.sessionId && !s.session.notes.some((n) => n.id === added.id)
          ? { ...s, session: { ...s.session, notes: [...s.session.notes, added] } }
          : s,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventSeq]);

  useEffect(() => {
    const t = setInterval(loadStatus, 30_000);
    return () => clearInterval(t);
  }, [loadStatus]);

  useEffect(() => {
    if (!status) return;
    window.tt.setTray(status.online ? 'running' : 'idle', status.online ? `${status.consultantName} è online per te` : 'CRM TecnoSide');
  }, [status?.online, status?.consultantName]);

  const consultant = status?.consultantName ?? 'Il consulente';
  const online = !!(status?.online && status.session);
  const sessions: SessionDto[] = report?.sessions ?? [];

  const byDay = useMemo(() => {
    const m: Record<string, SessionDto[]> = {};
    for (const s of sessions) (m[localDay(s.startedAt)] ??= []).push(s);
    return m;
  }, [sessions]);
  const marks = useMemo(() => {
    const m: Record<string, true> = {};
    for (const k of Object.keys(byDay)) m[k] = true;
    if (online) m[today] = true;
    return m;
  }, [byDay, online, today]);

  const todaySessions = month === thisMonth() ? (byDay[today] ?? []) : [];
  const listed = filter === 'day' && day ? (byDay[day] ?? []) : sessions;
  const listedMinutes = listed.reduce((a, s) => a + s.totalMinutes, 0);
  const listedAmount = listed.reduce((a, s) => a + s.amountCents, 0);

  const goToday = () => {
    setMonth(thisMonth());
    setDay(today);
    setFilter('day');
  };
  const pickDay = (k: string) => {
    if (k.slice(0, 7) !== month) setMonth(k.slice(0, 7));
    setDay(k);
    setFilter('day');
  };

  const subtitle =
    filter === 'live' ? (online ? `${consultant} è online per te` : `${consultant} non è in sessione`) : filter === 'day' && day ? dayLabel(day) : monthLabel(month);

  return (
    <div className="panels">
      <Panel title="Ore di lavoro" subtitle={me.clientName ? `Il lavoro fatto per ${me.clientName}` : 'Il lavoro fatto per te'}>
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <div>
          <Seg icon="inbox" label="In corso" count={online ? 1 : 0} tone={online ? 'good' : undefined} active={filter === 'live'} onClick={() => setFilter('live')} />
          <Seg icon="checkCircle" label="Oggi" count={todaySessions.length + (online ? 1 : 0)} active={filter === 'day' && day === today} onClick={goToday} />
          <Seg icon="layers" label="Tutto il mese" count={sessions.length} active={filter === 'month'} onClick={() => setFilter('month')} />
        </div>

        <ClockCard
          now={now}
          on={online}
          headline={online && status?.session ? elapsed(status.session.startedAt, now) : fmtClock(now)}
          sub={
            online ? (
              <>
                <b>{consultant}</b> · online per te
              </>
            ) : (
              fmtLongDate(now)
            )
          }
        />

        <Calendar
          month={month}
          selected={filter === 'day' ? day : null}
          marks={marks}
          onSelect={pickDay}
          onMonth={(m) => {
            setMonth(m);
            setDay(null);
            setFilter('month');
          }}
        />
      </Panel>

      <Panel title="Attività" subtitle={subtitle}>
        {filter === 'live' && online && status?.session && (
          <>
            <div className="card navy">
              <div className="hstack between wrap">
                <div>
                  <div className="eyebrow" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    {typeLabel(status.session.type)} · dalle {fmtTime(status.session.startedAt)}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 500 }}>{consultant} sta lavorando per te</div>
                </div>
                <div className="timer" style={{ fontSize: 28, fontWeight: 500 }}>
                  {elapsed(status.session.startedAt, now)}
                </div>
              </div>
            </div>
            <Section title="Note in tempo reale" count={status.session.notes.length} open={sections.open.notes} onToggle={() => sections.toggle('notes')}>
              {status.session.notes.length === 0 ? (
                <Empty icon="note">Le note del consulente compariranno qui man mano che le scrive.</Empty>
              ) : (
                status.session.notes.map((n) => <NoteRow key={n.id} note={n} />)
              )}
            </Section>
          </>
        )}

        {filter === 'live' && !online && (
          <>
            {!status ? (
              <div className="muted small">Caricamento…</div>
            ) : (
              <Empty icon="clock">{consultant} ora non sta lavorando per te: ti avvisiamo quando comincia.</Empty>
            )}
            <Section title="Sessioni di oggi" count={todaySessions.length} open={sections.open.today} onToggle={() => sections.toggle('today')}>
              <SessionsList api={api} sessions={todaySessions} showClient={false} emptyText="Nessuna sessione conclusa oggi." />
            </Section>
          </>
        )}

        {filter !== 'live' && (
          <>
            <div className="stats">
              <Stat value={listed.length} label="sessioni" />
              <Stat value={hm(listedMinutes)} label="ore" />
              <Stat value={euro(listedAmount)} label="importo" navy />
            </div>
            <SessionsList api={api} sessions={listed} showClient={false} />
          </>
        )}
      </Panel>
    </div>
  );
}
